import { createFileRoute } from "@tanstack/react-router"
import { parseMarketplaceId } from "@nova-ai/shared"

import { AppShell } from "../components/layout/app-shell"
import { CampaignTableSection } from "../components/dashboard/campaign-table-card"
import { MarketplaceMark } from "../components/marketplaces/marketplace-mark"
import { MarketplaceSwitch } from "../components/marketplaces/marketplace-switch"
import { humanizeMarketplaceId } from "../lib/marketplace"
import { useMarketplaceSummaries } from "../services/use-dashboard"
import { useRates } from "../services/use-rates"

/**
 * One marketplace's advertised products, read to decide a bid (#482), on its own
 * page.
 *
 * It used to sit on the marketplace dashboard beneath the revenue trajectory.
 * It moved for the reason its own period picker already stated: the table is
 * judged WEEKLY, against the Monday ads review, and the dashboard's rolling
 * window answers a different question. Two pickers on one page invited the
 * reading that one drove the other — the same reason the profit & loss statement
 * moved out.
 *
 * No `QueryState` wrapper: the section owns its read and already renders its
 * loading, unreported, and no-advertiser states.
 */
function CampaignsPage() {
  const { marketplace: raw } = Route.useParams()
  const marketplace = parseMarketplaceId(raw)
  const rates = useRates()
  // The campaign read carries no currency of its own, so it comes from the fleet
  // summaries — the same query the switcher above already makes, so this is a
  // cache hit rather than a second request.
  const summaries = useMarketplaceSummaries()
  const currency =
    summaries.data?.find((summary) => summary.marketplace === marketplace)?.currency ?? "EUR"

  return (
    <AppShell
      title="Campaigns"
      description={`Advertised products and the two pairs a bid is decided from — ${humanizeMarketplaceId(marketplace)}.`}
      icon={<MarketplaceMark marketplace={marketplace} />}
      actions={<MarketplaceSwitch marketplace={marketplace} to="/campaigns/$marketplace" />}
      fullWidth
    >
      <CampaignTableSection marketplace={marketplace} currency={currency} rates={rates.data} />
    </AppShell>
  )
}

export const Route = createFileRoute("/campaigns/$marketplace/")({ component: CampaignsPage })
